import { Graphics, Texture } from "pixi.js";
import { useState, useEffect } from "react";
import { TileImageConfig } from "../types/types";

// Global cache for cursor textures - shared across all TileCursor instances
const cursorTextureCache = new Map<string, Texture>();

// Generate a unique cache key based on cursor properties
function getCursorCacheKey(
  width: number,
  height: number,
  cornerRadius: number,
  borderGap: number,
  borderThickness: number,
  borderColor: string | number
): string {
  return `cursor_${width}_${height}_${cornerRadius}_${borderGap}_${borderThickness}_${borderColor}`;
}

// Convert hex color number to CSS color string
function colorToString(color: string | number): string {
  if (typeof color === "string") return color;
  return `#${color.toString(16).padStart(6, "0")}`;
}

// Generate a cursor texture using Canvas2D and cache it globally
function generateCursorTexture(
  width: number,
  height: number,
  cornerRadius: number,
  borderGap: number,
  borderThickness: number,
  borderColor: string | number
): Texture {
  const cacheKey = getCursorCacheKey(
    width,
    height,
    cornerRadius,
    borderGap,
    borderThickness,
    borderColor
  );

  // Return cached texture if it exists
  if (cursorTextureCache.has(cacheKey)) {
    return cursorTextureCache.get(cacheKey)!;
  }

  // Calculate canvas dimensions with padding for the border
  const padding = borderGap + borderThickness * 2;
  const canvasWidth = Math.ceil(width + padding * 2);
  const canvasHeight = Math.ceil(height + padding * 2);

  try {
    // Create a canvas to draw the cursor
    const canvas = document.createElement("canvas");
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      throw new Error("Failed to get canvas context");
    }

    // Set up the drawing context
    const rectX = padding;
    const rectY = padding;
    const rectWidth = width + borderThickness * borderGap;
    const rectHeight = height + borderThickness * borderGap;

    ctx.strokeStyle = colorToString(borderColor);
    ctx.lineWidth = borderThickness;

    // Draw rounded rectangle
    ctx.beginPath();
    if (cornerRadius > 0) {
      const radius = cornerRadius;
      ctx.moveTo(rectX - borderGap + radius, rectY - borderGap);
      ctx.lineTo(rectX - borderGap + rectWidth - radius, rectY - borderGap);
      ctx.arcTo(
        rectX - borderGap + rectWidth,
        rectY - borderGap,
        rectX - borderGap + rectWidth,
        rectY - borderGap + radius,
        radius
      );
      ctx.lineTo(
        rectX - borderGap + rectWidth,
        rectY - borderGap + rectHeight - radius
      );
      ctx.arcTo(
        rectX - borderGap + rectWidth,
        rectY - borderGap + rectHeight,
        rectX - borderGap + rectWidth - radius,
        rectY - borderGap + rectHeight,
        radius
      );
      ctx.lineTo(rectX - borderGap + radius, rectY - borderGap + rectHeight);
      ctx.arcTo(
        rectX - borderGap,
        rectY - borderGap + rectHeight,
        rectX - borderGap,
        rectY - borderGap + rectHeight - radius,
        radius
      );
      ctx.lineTo(rectX - borderGap, rectY - borderGap + radius);
      ctx.arcTo(
        rectX - borderGap,
        rectY - borderGap,
        rectX - borderGap + radius,
        rectY - borderGap,
        radius
      );
    } else {
      ctx.rect(
        rectX - borderGap,
        rectY - borderGap,
        rectWidth,
        rectHeight
      );
    }
    ctx.stroke();

    // Create texture from canvas
    const texture = Texture.from(canvas);

    // Store in global cache (persists for app lifecycle)
    cursorTextureCache.set(cacheKey, texture);

    return texture;
  } catch (error) {
    console.warn("Error generating TileCursor texture:", error);
    return Texture.WHITE;
  }
}

export function TileCursor({
  tileImageConfig,
  borderGap,
  borderThickness,
  borderColor,
}: {
  tileImageConfig: TileImageConfig;
  borderGap: number;
  borderThickness: number;
  borderColor: string | number;
}) {
  const { width, height, cornerRadius } = tileImageConfig;
  const [texture, setTexture] = useState<Texture | null>(null);

  useEffect(() => {
    // Get or generate the shared texture
    const sharedTexture = generateCursorTexture(
      width,
      height,
      cornerRadius || 0,
      borderGap,
      borderThickness,
      borderColor
    );
    setTexture(sharedTexture);
  }, [width, height, cornerRadius, borderGap, borderThickness, borderColor]);

  // Calculate sprite position to match original graphics offset
  const padding = borderGap + borderThickness * 2;
  const spriteX = -padding;
  const spriteY = -padding;

  // Fallback to drawing graphics if texture generation fails
  if (!texture || texture === Texture.WHITE) {
    return (
      <pixiGraphics
        draw={(g: Graphics) => {
          try {
            g.clear();
            g.setStrokeStyle({
              width: borderThickness,
              color: borderColor,
            });
            g.roundRect(
              -borderGap,
              -borderGap,
              width + borderThickness * borderGap,
              height + borderThickness * borderGap,
              cornerRadius ? cornerRadius : 0
            );
            g.stroke();
          } catch (error) {
            console.warn("Error drawing TileCursor:", error);
          }
        }}
      />
    );
  }

  // Use the globally cached texture via a sprite - all instances share this texture!
  return <pixiSprite texture={texture} x={spriteX} y={spriteY} />;
}
